// Quality tier -> concrete RenderConfig, plus prefers-reduced-motion gating.
// Pure, NO PixiJS / browser API.

import type { VisualQualityTier } from '@shared/voice';
import { SPAWN_COUNT } from './spawn';

/** Strike-ripple pool capacity per visual quality tier (0 = ripples disabled). */
export const RIPPLE_POOL: Record<VisualQualityTier, number> = {
  high: 6,
  medium: 4,
  low: 0,
};

export interface RenderConfig {
  resolution: number; // devicePixelRatio cap (high<=2, med<=1.5, low=1)
  bloomEnabled: boolean;
  bloomQuality: number; // AdvancedBloom quality (high 8, med 4, low 0)
  ripplePoolSize: number; // high 6, med 4, low 0
  spriteCount: number; // high 22, med 16, low 10
  flowEnabled: boolean;
  flowUpdateDivisor: number; // recompute flow every N frames (high 1, med 2, low 4)
  displacement: boolean; // lens displacement filter (high only)
  reducedMotion: boolean;
}

const TIERS: Record<VisualQualityTier, Omit<RenderConfig, 'reducedMotion'>> = {
  high: {
    resolution: 2,
    bloomEnabled: true,
    bloomQuality: 8,
    ripplePoolSize: RIPPLE_POOL.high,
    spriteCount: SPAWN_COUNT.high,
    flowEnabled: true,
    flowUpdateDivisor: 1,
    displacement: true,
  },
  medium: {
    resolution: 1.5,
    bloomEnabled: true,
    bloomQuality: 4,
    ripplePoolSize: RIPPLE_POOL.medium,
    spriteCount: SPAWN_COUNT.medium,
    flowEnabled: true,
    flowUpdateDivisor: 2,
    displacement: false,
  },
  low: {
    resolution: 1,
    bloomEnabled: false,
    bloomQuality: 0,
    ripplePoolSize: RIPPLE_POOL.low,
    spriteCount: SPAWN_COUNT.low,
    flowEnabled: true,
    flowUpdateDivisor: 4,
    displacement: false,
  },
};

/**
 * Resolve a RenderConfig for the tier. When `reducedMotion` is true, motion-bearing
 * features are zeroed: flow disabled, displacement off, ripple pool 0. (Sprite count,
 * bloom, and resolution are untouched — reduced-motion concerns animation, not fidelity.)
 */
export function qualityTier(
  tier: VisualQualityTier,
  reducedMotion = false,
): RenderConfig {
  const base = TIERS[tier];
  const cfg: RenderConfig = { ...base, reducedMotion };
  if (reducedMotion) {
    cfg.flowEnabled = false;
    cfg.displacement = false;
    cfg.ripplePoolSize = 0;
  }
  return cfg;
}

/** What reduced-motion zeroes, as a pure predicate set (for tests/consumers). */
export interface MotionGate {
  flow: boolean; // flow field active
  breathing: boolean; // per-sprite breathing active
}

/** Pure: given reducedMotion, which animated systems run. */
export function motionGate(reducedMotion: boolean): MotionGate {
  return {
    flow: !reducedMotion,
    breathing: !reducedMotion,
  };
}
